import { ReadonlyVec2, ReadonlyVec3, vec3, vec4 } from 'gl-matrix';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { TargetSphere } from '../../../data/common/customs/TargetSphere';
import { PlaneView } from '../../../view/views/PlaneView';
import { BrushCursorAction, BrushCursorActionState } from './BrushCursorAction';
import { View } from '~/engine/view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';

/**
 * {@link StaticBrushCursorActionState} is a {@link BrushCursorActionState} that stores the state of an active {@link StaticBrushCursorAction}.\
 * It stores a {@link TargetSphere} object, which is used as the cursor object.\
 * And provides methods to manipulate that {@link TargetSphere} object.
 */
export class StaticBrushCursorActionState extends BrushCursorActionState {
    /**
     * {@link StaticBrushCursorActionState} constructor, which takes a {@link PlaneView} and a {@link TargetSphere} as arguments.\
     * It calls the {@link BrushCursorActionState super} {@link BrushCursorActionState.constructor constructor} with the {@link view} and {@link cursorObject} as arguments.\
     * It also adds the {@link TargetSphere} to the {@link Environment.env Environment} as a {@link Environment.env.customObjects customObject}.\
     * And updates the {@link ViewManager} with the {@link VolumeViewUpdateEvent} of the {@link TargetSphere}.
     * @param view {@link PlaneView} on which the {@link StaticBrushCursorAction} is started.
     * @param cursorObject {@link TargetSphere} that is used as the cursor object.
     */
    public constructor(view: PlaneView, cursorObject: TargetSphere) {
        super(view, cursorObject);
        view.addCustomObject(cursorObject);
        view.renderLayers();
        this._targetSphere = cursorObject;
    }

    private _targetSphere: TargetSphere;

    /**
     * @override {@link BrushCursorActionState.setMousePosition}
     * Method that is called by the {@link StaticBrushCursorAction} to inform the {@link StaticBrushCursorActionState} of the new {@link ReadonlyVec2 mousePosition}.\
     * If {@link mousePosition} is defined, it converts the {@link mousePosition} to {@link ReadonlyVec3 PatientSpaceCoordinates} and sets the translation of the {@link TargetSphere} to that position.\
     * It then updates the {@link ViewManager} with the {@link VolumeViewUpdateEvent} of the {@link TargetSphere}.
     * @param mousePosition {@link ReadonlyVec2 Client pixel coordinates} of the mouse in between [0, pixelSize - 1].
     */
    public setMousePosition(mousePosition?: ReadonlyVec2): void {
        if (mousePosition) {
            const patientCoordinates =
                this._planeView.clientToPatientCoordinates(mousePosition);
            this._targetSphere.setPosition(patientCoordinates);
        }
        this._planeView.renderLayers();
    }

    public updateBrushSize(brushSize: number): void {
        this._targetSphere.resize(brushSize);
        this._planeView.renderLayers();
    }

    public clearMemory() {
        this._planeView.removeCustomObject(this._targetSphere);
        this._planeView.renderLayers();
    }
}

/**
 * {@link StaticBrushCursorAction} is a {@link BrushCursorAction} that provides brush cursor functionality.\
 * The cursor is either a 2D circle or a 3D sphere, depending on the mode of the tool.\
 * The {@link StaticBrushCursorAction} is used by the {@link StaticBrushTool}.
 *
 * {@link StaticBrushCursorAction} uses a {@link TargetSphere} as the cursor object. For more details
 * @see {@link StaticBrushCursorActionState}
 */
export class StaticBrushCursorAction extends BrushCursorAction {
    /**
     * {@link StaticBrushCursorAction} constructor that calls the {@link BrushCursorAction super} {@link BrushCursorAction.constructor constructor} with the {@link brushSize} as an argument.
     * @param brushSize Diameter of the brush in millimeters.
     */
    public constructor(
        protected readonly image: ImageObject,
        brushSize: number,
        protected readonly _color: ReadonlyVec3 = vec3.fromValues(1, 0, 0)
    ) {
        super(brushSize);
    }

    private _state: StaticBrushCursorActionState | undefined;

    public onMouseMove(
        _coordinates: ReadonlyVec2,
        _buttons: number,
        _view: View
    ): EventHandlerResult {
        if (this._state && this._state.view !== _view) {
            this._state.clearMemory();
            this._state = undefined;
        }
        if (!this._state) {
            this._state = this.startAction(_view as PlaneView, _coordinates);
        } else {
            this._state.setMousePosition(_coordinates);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Method to start the {@link StaticBrushCursorAction}.\
     * It fetches an {@link Environment.image image} from the {@link Environment}, which defines the space that the {@link StaticBrushCursorAction cursor} should reside in.\
     * And creates a {@link TargetSphere} object, which is used as the cursor object.\
     * The {@link StaticBrushCursorActionState} is then created and returned with the {@link PlaneView} and the {@link TargetSphere} as arguments.\
     * If however, the {@link Environment.image image} is undefined, then the {@link StaticBrushCursorAction} is not started, and undefined is returned.
     * @param view {@link PlaneView} on which the {@link StaticBrushCursorAction} is started.
     * @param mousePosition {@link ReadonlyVec2 Client pixel coordinates} of the mouse, in between [0, pixelSize - 1], at the time the cursor action is started.
     * @returns If the method was able to fetch an {@link Environment.image image} from the {@link Environment}, then it returns a new {@link StaticBrushCursorActionState} instance.\
     * Otherwise it returns undefined.
     */
    protected startAction(
        view: PlaneView,
        mousePosition: ReadonlyVec2
    ): StaticBrushCursorActionState {
        const patientCoordinates =
            view.clientToPatientCoordinates(mousePosition);
        const staticSphere = new TargetSphere(
            this.image,
            patientCoordinates,
            this.brushSize,
            vec4.fromValues(this._color[0], this._color[1], this._color[2], 1)
        );
        return new StaticBrushCursorActionState(view, staticSphere);
    }

    public stopAction(): EventHandlerResult {
        if (this._state) {
            this._state.clearMemory();
            this._state = undefined;
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    /**
     * Method to update the diameter of the brush stored within {@link StaticBrushCursorAction} and {@link StaticBrushCursorActionState}.
     * @param brushSize The new diameter of the brush in millimeters.
     */
    public updateBrushSize(brushSize: number): void {
        super.updateBrushSize(brushSize);
        this._state?.updateBrushSize(brushSize);
    }
}
